import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';
import cloudApi from '../../../app/api/cloudApi';

export default function IPSettingsTab({ deviceId, siteId, accountId, token }) {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    useDHCP: false,
    ipAddr: '',
    gateway: '',
    subnetMask: '',
    port: 51211,
    connectionMode: 0,
    serverAddr: '',
    serverPort: 51212,
    sslServerPort: 51213,
    useDNS: false,
    dnsServer: '',
    serverURL: '',
    mtuSize: 1514,
    baseband: 1,
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (deviceId && token) {
      loadConfig();
    }
  }, [deviceId, token]);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await cloudApi.getIPConfig(token, siteId, accountId, deviceId);
      if (response.data) {
        setConfig(response.data);
      }
    } catch (err) {
      console.error('Error loading IP config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to load IP configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await cloudApi.setIPConfig(token, siteId, accountId, {
        deviceId: deviceId,
        ...config,
      });
      enqueueSnackbar('IP configuration saved successfully', { variant: 'success' });
    } catch (err) {
      console.error('Error saving IP config:', err);
      const errorMessage = err.response?.data || err.message || 'Failed to save IP configuration';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <Typography variant="h6">Network Configuration</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={config.useDHCP}
              onChange={(e) => handleFieldChange('useDHCP', e.target.checked)}
            />
          }
          label="Use DHCP (Automatic IP Assignment)"
        />

        {!config.useDHCP && (
          <>
            <TextField
              fullWidth
              label="IP Address"
              value={config.ipAddr}
              onChange={(e) => handleFieldChange('ipAddr', e.target.value)}
              placeholder="192.168.1.100"
              helperText="Static IP address for the device"
            />

            <TextField
              fullWidth
              label="Gateway"
              value={config.gateway}
              onChange={(e) => handleFieldChange('gateway', e.target.value)}
              placeholder="192.168.1.1"
              helperText="Default gateway IP address"
            />

            <TextField
              fullWidth
              label="Subnet Mask"
              value={config.subnetMask}
              onChange={(e) => handleFieldChange('subnetMask', e.target.value)}
              placeholder="255.255.255.0"
              helperText="Network subnet mask"
            />
          </>
        )}

        <TextField
          fullWidth
          type="number"
          label="Device Port"
          value={config.port}
          onChange={(e) => handleFieldChange('port', parseInt(e.target.value) || 51211)}
          helperText="TCP port for device communication (Default: 51211)"
          inputProps={{ min: 1, max: 65535 }}
        />

        <Divider />

        <Typography variant="h6">Server Communication</Typography>

        <FormControl fullWidth>
          <InputLabel id="connection-mode-label">Connection Mode</InputLabel>
          <Select
            labelId="connection-mode-label"
            value={config.connectionMode}
            label="Connection Mode"
            onChange={(e) => handleFieldChange('connectionMode', e.target.value)}
          >
            <MenuItem value={0}>Server to Device</MenuItem>
            <MenuItem value={1}>Device to Server</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Server Address"
          value={config.serverAddr}
          onChange={(e) => handleFieldChange('serverAddr', e.target.value)}
          placeholder="192.168.1.10"
          helperText="IP address of the server"
        />

        <TextField
          fullWidth
          type="number"
          label="Server Port"
          value={config.serverPort}
          onChange={(e) => handleFieldChange('serverPort', parseInt(e.target.value) || 51212)}
          helperText="TCP port for server connection (Default: 51212)"
          inputProps={{ min: 1, max: 65535 }}
        />

        <TextField
          fullWidth
          type="number"
          label="SSL Server Port"
          value={config.sslServerPort}
          onChange={(e) => handleFieldChange('sslServerPort', parseInt(e.target.value) || 51213)}
          helperText="SSL port for secure server connection (Default: 51213)"
          inputProps={{ min: 1, max: 65535 }}
        />

        <Divider />

        <Typography variant="h6">DNS Configuration</Typography>

        <FormControlLabel
          control={
            <Switch
              checked={config.useDNS}
              onChange={(e) => handleFieldChange('useDNS', e.target.checked)}
            />
          }
          label="Use DNS"
        />

        {config.useDNS && (
          <>
            <TextField
              fullWidth
              label="DNS Server"
              value={config.dnsServer}
              onChange={(e) => handleFieldChange('dnsServer', e.target.value)}
              placeholder="8.8.8.8"
              helperText="DNS server IP address"
            />

            <TextField
              fullWidth
              label="Server URL"
              value={config.serverURL}
              onChange={(e) => handleFieldChange('serverURL', e.target.value)}
              placeholder="server.example.com"
              helperText="Server domain name (if using DNS)"
            />
          </>
        )}

        <Divider />

        <Typography variant="h6">Advanced Settings</Typography>

        <TextField
          fullWidth
          type="number"
          label="MTU Size"
          value={config.mtuSize}
          onChange={(e) => handleFieldChange('mtuSize', parseInt(e.target.value) || 1514)}
          helperText="Maximum Transmission Unit size (1078-1514, Default: 1514)"
          inputProps={{ min: 1078, max: 1514 }}
        />

        <FormControl fullWidth>
          <InputLabel id="baseband-label">Ethernet Baseband</InputLabel>
          <Select
            labelId="baseband-label"
            value={config.baseband}
            label="Ethernet Baseband"
            onChange={(e) => handleFieldChange('baseband', e.target.value)}
          >
            <MenuItem value={0}>10BASE-T (10 Mbps)</MenuItem>
            <MenuItem value={1}>100BASE-T (100 Mbps)</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button variant="outlined" onClick={loadConfig} disabled={loading || saving}>
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Icon icon="solar:diskette-bold" />}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

